import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Spinner,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  useDisclosure,
  Badge,
} from "@chakra-ui/react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import HybridBottomSheetDrawer from "../../components/HybridBottomSheetDrawer";
import { TiLocationArrow } from "react-icons/ti";
import {
  BusStop,
  BusTime,
  createBusSVG,
  useAllBusLocations,
  useAllBusStops,
  useNearbyBusStops,
} from "../../data/buses.api";
import BusStopPreview from "../../components/buses/BusStopPreview";
import { MapContext } from "../../context/map.context";
import { useNavigate } from "react-router-dom";
import QueryStateWrapper from "../../components/QueryStateWrapper";
import BackButton from "../../components/BackButton";
import { MapPin } from "../../components/Map";
import LocationButton from "../../components/LocationButton";

const NearbyBusStops = () => {
  const {
    setPins,
    watchLocation,
    reset,
    personalLocation,
    location,
    locationError,
  } = useContext(MapContext);
  const {
    data: busStops,
    error,
    isLoading,
  } = useNearbyBusStops(location?.lng, location?.lat);
  const { data: buses } = useAllBusLocations();
  const [busStopsLoading, setBusStopsLoading] = useState(true);
  const [busStopsData, setBusStopsData] = useState<BusStop[]>();
  const [busStopPins, setBusStopPins] = useState<MapPin[]>([]);
  const [busPins, setBusPins] = useState<MapPin[]>([]);  const [selectedBus, setSelectedBus] = useState<BusTime | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading) {
      setBusStopsLoading(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (busStops) {
      setBusStopsData(busStops);
    }
  }, [busStops]);

  useEffect(() => {
    if (busStops) {
      setBusStopPins(
        busStops.map((busStop) => ({
          lng: parseFloat(busStop.longitude),
          lat: parseFloat(busStop.latitude),
          onClick: () => navigate(`/buses/${busStop.atcoCode}`),
        }))
      );
    }
  }, [busStops, navigate]);

  useEffect(() => {
    if (buses) {
      setBusPins(
        buses
          .filter((busTime) => busTime.vehicleLocation !== null)
          .map((busTime) => ({
            anchor: "top",
            shake: "circle",
            size: 24,
            imgUrl: createBusSVG(
              busTime.line,
              busTime.vehicleLocation?.bearing
                ? parseInt(busTime.vehicleLocation.bearing)
                : null
            ),
            imgScale: 5,
            color: "transparent",
            lng: busTime.vehicleLocation!.longitude,
            lat: busTime.vehicleLocation!.latitude,
            onClick: () => {
              setSelectedBus(busTime);
              onOpen();
            },
          }))
      );
    }
  }, [buses, navigate]);

  useEffect(() => {
    setPins([...busStopPins, ...busPins]);
  }, [busStopPins, busPins, setPins]);

  useEffect(() => {
    watchLocation();
  }, [watchLocation]);

  useEffect(() => {
    return reset;
  }, [reset]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Bus Information</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedBus && (
              <Stack spacing={3}>
                <Flex align="center" gap={2}>
                  <Text fontWeight="bold">Service:</Text>
                  <Badge colorScheme="blue" fontSize="lg" px={3} py={1}>
                    {selectedBus.line}
                  </Badge>
                </Flex>
                <Box>
                  <Text fontWeight="bold">Destination:</Text>
                  <Text>{selectedBus.destinationName}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Operator:</Text>
                  <Text>{selectedBus.source}</Text>
                </Box>
                {selectedBus.expectedDepartureTime && (
                  <Box>
                    <Text fontWeight="bold">Next Departure:</Text>
                    <Text>
                      {new Date(
                        selectedBus.expectedDepartureTime
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Text>
                  </Box>
                )}
              </Stack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <HybridBottomSheetDrawer>
        <BackButton />

        <Box p={8}>
          <Flex mb={4} justify="space-between" align="center">
            <Heading size="md">Bus Stops</Heading>
            <LocationButton variant="outline">Nearby</LocationButton>
          </Flex>

          <QueryStateWrapper
            isLoading={busStopsLoading}
            error={error}
            data={busStopsData}
          >
            <Stack spacing={3}>
              {busStopsData?.map((busStop) => (
                <BusStopPreview key={busStop.atcoCode} busStop={busStop} />
              ))}
            </Stack>
          </QueryStateWrapper>
        </Box>
      </HybridBottomSheetDrawer>
    </>
  );
};

export default NearbyBusStops;
